import { useState, useCallback } from 'react';
import { billingApi, plansApi } from '../api/billing';
import type { Plan, BillingCharge } from '../api/billing';

export function usePlans() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await plansApi.list();
      setPlans(res.data.data);
    } catch {
      setError('Failed to load plans.');
    } finally {
      setLoading(false);
    }
  }, []);

  return { plans, loading, error, fetchPlans };
}

export function useBilling() {
  const [charge, setCharge] = useState<BillingCharge | null>(null);
  const [loading, setLoading] = useState(false);
  const [subscribing, setSubscribing] = useState<number | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await billingApi.status();
      setCharge(res.data.data);
    } catch {
      setError('Failed to load billing status.');
    } finally {
      setLoading(false);
    }
  }, []);

  const subscribe = useCallback(async (planId: number) => {
    setSubscribing(planId);
    setError(null);
    try {
      const host = new URLSearchParams(window.location.search).get('host');
      const res = await billingApi.subscribe(planId, host);
      const url = res.data.data.confirmation_url;
      window.open(url, '_top');
    } catch (err: any) {
      const msg = err?.message ?? 'Subscription failed.';
      setError(msg);
    } finally {
      setSubscribing(null);
    }
  }, []);

  const activateCharge = useCallback(async (chargeId: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await billingApi.activate(chargeId);
      setCharge(res.data.data);
      return res.data.data;
    } catch (err: any) {
      const msg = err?.message ?? 'Activation failed.';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const cancel = useCallback(async () => {
    setCancelling(true);
    setError(null);
    try {
      await billingApi.cancel();
      setCharge(null);
    } catch (err: any) {
      const msg = err?.message ?? 'Cancellation failed.';
      setError(msg);
    } finally {
      setCancelling(false);
    }
  }, []);

  return {
    charge, loading, subscribing, cancelling, error,
    fetchStatus, subscribe, activateCharge, cancel, setError,
  };
}
